import { getNodeIdFromCuid } from "./getNodeIdFromCuid";

const CUID_PATTERN = /^00000000-0000-c000-8000-(\d{12})$/;

/**
 * Determines if a string is a CUID format
 * @param input - The string to test
 * @returns true if the input matches CUID pattern, false otherwise
 */
function isCuid(input: string): boolean {
  return CUID_PATTERN.test(input);
}

/**
 * Utility function to normalize a zap node ID from various input formats
 *
 * This function handles:
 * - CUID format: "00000000-0000-c000-8000-000000123456" -> "123456"
 * - Number: 123456 -> "123456"
 * - String digits: "123456" -> "123456"
 *
 * @param zapId - The zap ID (can be CUID, number, or string)
 * @returns The normalized zap ID as a string
 *
 * @example
 * ```ts
 * // CUID format
 * normalizeZapNodeId("00000000-0000-c000-8000-000000123456") // "123456"
 *
 * // Number
 * normalizeZapNodeId(123456) // "123456"
 *
 * // String digits
 * normalizeZapNodeId("123456") // "123456"
 *
 * // Other values
 * normalizeZapNodeId(undefined) // "undefined"
 * normalizeZapNodeId("") // ""
 * normalizeZapNodeId("invalid-format") // "invalid-format"
 * ```
 */
export function normalizeZapNodeId(zapId?: string | number | null): string {
  if (!zapId) {
    return String(zapId);
  }

  // Convert number to string
  if (typeof zapId === "number") {
    return String(zapId);
  }

  // Handle string input
  if (typeof zapId === "string") {
    // Strip "zap:" prefix if present (from convertToAssetId format used by AssetRow)
    const cleaned = zapId.startsWith("zap:") ? zapId.slice(4) : zapId;

    // Check if it's a CUID and extract the node ID
    if (isCuid(cleaned)) {
      try {
        return String(getNodeIdFromCuid(cleaned));
      } catch {
        // If CUID parsing fails, return stringified value
        return String(cleaned);
      }
    }

    // For strings that only consist of digits, return as-is
    if (/^\d+$/.test(cleaned)) {
      return cleaned;
    }

    // For any other string value, return stringified
    return String(cleaned);
  }

  // For any other format, return stringified
  return String(zapId);
}

export function normalizeZapNodeIds(nodeIds: (number | string)[]): string[] {
  return nodeIds.map((nodeId: number | string) => normalizeZapNodeId(nodeId));
}
